import axios from "axios";
import { deepClone } from "@/utils/deepClone";
import customerAccounts from "@/services/customerAccounts";
import { addQuoteContact, editQuoteContact, removeQuoteContact } from "@/services/quotes";
import lineItemService from "@/services/lineItems";
import { validateQuoteCharges, validateQuoteStops } from "@/validation/quotes/pricingValidator";

export const namespaced = true;

export const state = {
    quoteInProgress: getSavedState("quoteInProgress"),
    availableCustomerAccounts: [],
    toggleCreateCustomerAccount: false,
    selectedCustomerAccount: null,
    isACustomer: false,
    selectedCustomer: {},
    selectedMarket: {},
    autoDetectedMarket: {},
    selectedGarage: {},
    shouldSplitTrip: false,
    pendingQuoteContacts: {},
    // Line items state for bid/category quotes with new charge model
    tripLineItems: {}, // Keyed by trip index: { 0: [...lineItems], 1: [...lineItems] }
    tripTotals: {}, // Keyed by trip index: { 0: 1263.73, 1: 2500.00 }
    lineItemsQuoteTotal: 0, // Total for all trips from the line items API
    lineItemsLoading: false, // Loading state for line items calculation
    lineItemsError: null, // Error state for line items calculation
};

// TODO: get rid of this stuff
export const getters = {
    quoteInProgress(state) {
        return state.quoteInProgress;
    },
    availableCustomerAccounts(state) {
        return state.availableCustomerAccounts;
    },
    getCreateCustomerAccount(state) {
        return state.toggleCreateCustomerAccount;
    },
    selectedCustomerAccount(state) {
        return state.selectedCustomerAccount;
    },
    isACustomer(state) {
        return state.isACustomer;
    },
    selectedCustomer(state) {
        return state.selectedCustomer;
    },
    getSelectedMarket: (state) => (index) => {
        return state.selectedMarket[index];
    },
    getSelectedMarketChanges(state) {
        return state.selectedMarket;
    },
    getAutoDetectedNearestMarketChanges(state) {
        return state.autoDetectedMarket;
    },
    getSelectedGarage: (state) => (index) => {
        return state.selectedGarage[index];
    },
    getGarageChanges(state) {
        return state.selectedGarage;
    },
    getShouldSplitTrip(state) {
        return state.shouldSplitTrip;
    },
    getPendingQuoteContacts: (state) => state.pendingQuoteContacts,
    // Line items getters
    getTripLineItems: (state) => (tripIndex) => {
        return state.tripLineItems[tripIndex] || [];
    },
    getTripTotal: (state) => (tripIndex) => {
        return state.tripTotals[tripIndex] || 0;
    },
    getAllTripLineItems: (state) => state.tripLineItems,
    getLineItemsQuoteTotal: (state) => state.lineItemsQuoteTotal,
    getLineItemsLoading: (state) => state.lineItemsLoading,
    getLineItemsError: (state) => state.lineItemsError,
};

export const mutations = {
    SAVE_QUOTE_IN_PROGRESS(state, quote) {
        if (!quote) {
            state.quoteInProgress = {};
            deleteState("quoteInProgress");
            return;
        }
        state.quoteInProgress = deepClone(quote);
        saveState("quoteInProgress", quote);
    },
    CLEAR_QUOTE_IN_PROGRESS(state) {
        state.quoteInProgress = null;
        deleteState("quoteInProgress");
    },
    SET_TOGGLE_CUSTOMER_ACCOUNT_CREATION(state) {
        state.toggleCreateCustomerAccount = !state.toggleCreateCustomerAccount;
        return state.toggleCreateCustomerAccount;
    },
    SET_IS_CUSTOMER(state) {
        state.isACustomer = false;
        return state.isACustomer;
    },
    SET_SELECTED_CUSTOMER(state, customer) {
        state.selectedCustomer = customer;
        return state.selectedCustomer;
    },
    ADD_AVAILABLE_ACCOUNTS(state, accounts) {
        if (Array.isArray(accounts)) {
            state.availableCustomerAccounts = state.availableCustomerAccounts.concat(accounts);
        } else {
            state.availableCustomerAccounts.push(accounts);
        }

        return state.availableCustomerAccounts;
    },
    CLEAR_SELECTED_MARKETS(state) {
        state.selectedMarket = {};
    },
    SET_SELECTED_MARKET(state, { market, index = 0 }) {
        const copiedMarket = JSON.parse(JSON.stringify(state.selectedMarket));
        copiedMarket[index] = market;

        state.selectedMarket = copiedMarket;
    },
    SET_AUTO_DETECTED_NEAREST_MARKET(state, { market, index = 0 }) {
        const copiedMarket = JSON.parse(JSON.stringify(state.autoDetectedMarket));
        copiedMarket[index] = market;

        state.autoDetectedMarket = copiedMarket;
    },
    SET_SELECTED_GARAGE(state, { garage, index = 0 }) {
        const copiedGarage = JSON.parse(JSON.stringify(state.selectedGarage));
        copiedGarage[index] = garage;

        state.selectedGarage = copiedGarage;
    },
    SET_SHOULD_SPLIT_TRIP(state, value) {
        state.shouldSplitTrip = value;
    },
    ADD_PENDING_QUOTE_CONTACT(state, { customerRoles, customer }) {
        const contactKey = customer?.externalId;

        state.pendingQuoteContacts[contactKey] = {
            customerRoles,
            customer: {
                id: customer?.id || null,
                firstName: customer?.firstName || "",
                lastName: customer?.lastName || "",
                email: customer?.email || "",
                phone: customer?.phone || "",
                externalId: customer?.externalId,
            },
        };
    },
    EDIT_PENDING_QUOTE_CONTACT(state, { customer, customerRoles }) {
        const contactKey = customer?.externalId;

        if (!contactKey || !state.pendingQuoteContacts[contactKey]) {
            console.warn("Cannot edit contact - not found in pending contacts", customer);
            return;
        }

        const updatedCustomer = {
            ...state.pendingQuoteContacts[contactKey].customer,
            firstName: customer?.firstName || state.pendingQuoteContacts[contactKey].customer.firstName,
            lastName: customer?.lastName || state.pendingQuoteContacts[contactKey].customer.lastName,
            email: customer?.email || state.pendingQuoteContacts[contactKey].customer.email,
            phone: customer?.phone || state.pendingQuoteContacts[contactKey].customer.phone,
            id: customer?.id !== undefined ? customer.id : state.pendingQuoteContacts[contactKey].customer.id,
            externalId: customer?.externalId || state.pendingQuoteContacts[contactKey].customer.externalId,
        };

        state.pendingQuoteContacts[contactKey] = {
            customerRoles: {
                ...customerRoles,
                userExternalId: updatedCustomer.externalId,
            },
            customer: updatedCustomer,
        };
    },
    REMOVE_PENDING_QUOTE_CONTACT(state, { customer }) {
        const contactKey = customer?.externalId;

        if (!contactKey) {
            console.warn("Cannot remove contact - no externalId provided", customer);
            return;
        }

        delete state.pendingQuoteContacts[contactKey];
    },
    CLEAR_PENDING_QUOTE_CONTACTS(state) {
        state.pendingQuoteContacts = {};
    },
    // Line items mutations
    SET_TRIP_LINE_ITEMS(state, { tripIndex, lineItems }) {
        state.tripLineItems = {
            ...state.tripLineItems,
            [tripIndex]: lineItems,
        };
    },
    SET_ALL_TRIP_LINE_ITEMS(state, tripLineItems) {
        state.tripLineItems = tripLineItems;
    },
    SET_TRIP_TOTALS(state, tripTotals) {
        state.tripTotals = tripTotals;
    },
    SET_LINE_ITEMS_QUOTE_TOTAL(state, total) {
        state.lineItemsQuoteTotal = total;
    },
    SET_LINE_ITEMS_LOADING(state, loading) {
        state.lineItemsLoading = loading;
    },
    SET_LINE_ITEMS_ERROR(state, error) {
        state.lineItemsError = error;
    },
    CLEAR_LINE_ITEMS(state) {
        state.tripLineItems = {};
        state.lineItemsQuoteTotal = 0;
        state.lineItemsError = null;
    },
};

export const actions = {
    // TODO: set the default state in the state definition above,
    //   then set up a getter for this.
    quotesListView(store, params) {
        const {
            // companyId,
            // quotes,
            sorts,
            filters,
            additionalQueries,
            pageSize = 10,
            page = 1,
        } = params;

        // keep this one around ...
        // let query = `companyId=${companyId}&quotes=${quotes}&page=${page}&pageSize=${pageSize}`

        let query = `page=${page}&pageSize=${pageSize}`;
        if (sorts) {
            query += `&${sorts}`;
        }
        if (filters) {
            query += `&${filters}`;
        }
        if (additionalQueries) {
            query += `&${additionalQueries}`;
        }
        query = encodeURI(query);
        const url = `/tables/quotes?${query}&v2=true`;
        return axios.get(url);
    },
    quotesTableExport(store, params) {
        const { sorts, filters } = params;

        let query = "";
        if (sorts) {
            query += sorts;
        }
        if (filters) {
            query += query.length ? `&${filters}` : `${filters}`;
        }
        query = encodeURI(query);
        const url = `/tables/quotes/export?${query}`;
        return axios.get(url, { responseType: "blob" });
    },
    checkManagedId(store, payload) {
        const url = `/v2/quotes/checkManagedId`;
        return axios.post(url, payload);
    },
    getV2Quote(store, quoteId) {
        const url = `/v2/quotes/${quoteId}`;

        return axios.get(url);
    },
    duplicateQuote(store, params) {
        const { payload } = params;
        const url = `/v2/quotes/duplicate?sendEmail=no-email`;
        return axios.post(url, payload);
    },
    convert(store, params) {
        const { quoteId, payload } = params;

        if (payload?.paymentMethodData) {
            delete payload.paymentMethodData.cardNumber;
            delete payload.paymentMethodData.securityCode;
        }

        const url = `/quotes/${quoteId}/convert?showToCustomer=false`;
        return axios.post(url, payload);
    },
    saveQuoteInProgress({ commit }, quote) {
        commit("SAVE_QUOTE_IN_PROGRESS", quote);
    },
    saveQuote(store, quote) {
        const { emailPreference = "no-email" } = quote;
        delete quote.emailPreference;
        return axios.post(`/quotes?sendEmail=${emailPreference}`, quote);
    },
    deleteQuote(store, quoteId) {
        const url = `/quotes/${quoteId}`;

        return axios.delete(url);
    },
    markSoldOut(store, quoteId) {
        const url = `/quotes/markSoldOut/${quoteId}`;

        return axios.get(url);
    },
    removeDraftStatus(store, quoteId) {
        const url = `/quotes/v2/unset-draft-quote/${quoteId}`;
        return axios.patch(url);
    },
    togglePendingConfirmation(store, quoteId) {
        const url = `/quotes/togglePendingConfirmation/${quoteId}`;

        return axios.post(url);
    },
    getQuotePricingById(store, params) {
        return axios.get(`/v2/pricings/${params.quoteId}?biddingCompanyId=${params.companyId}&needsManualReferral=${params.needsManualReferral}`);
    },
    getQuotePricing(store, quote) {
        if (quote.priceForAllPricingMethods === undefined) {
            quote.priceForAllPricingMethods = true;
        }

        // Validate that all charges have non-null chargeTypeId and chargeType
        if (!validateQuoteCharges(quote)) {
            return Promise.resolve();
        }

        // Validate that all stops have either pickupDate or dropoffDate
        if (!validateQuoteStops(quote)) {
            return Promise.resolve();
        }

        return axios.post(`/v2/pricings`, quote);
    },
    toggleCreateCustomerAccount({ commit }) {
        return commit("SET_TOGGLE_CUSTOMER_ACCOUNT_CREATION");
    },
    setSelectedCustomer({ commit }, account) {
        return commit("SET_SELECTED_CUSTOMER", account);
    },
    async getCustomerAccounts(store) {
        const {
            data: { resultList },
        } = await customerAccounts.getCustomerAccounts({ pageSize: 100 }, { root: true });
        return store.commit("ADD_AVAILABLE_ACCOUNTS", resultList);
    },
    changeEmailCampaignStatus(store, { status, quoteId }) {
        const payload = {
            status,
        };
        const url = `/quotes/email/${quoteId}`;
        return axios.post(url, payload);
    },
    getQuoteTripEstimations(store, payload) {
        const url = `/tripEstimations`;
        return axios.post(url, payload);
    },
    getQuoteSummary(store, payload) {
        const url = `/quotes/summary`;
        return axios.get(url, payload);
    },
    quoteTimeSeriesCustomerData(store, { customerId, payload }) {
        return axios.post(`/quotes/timeSeries/${customerId}/customer`, payload);
    },
    clearSelectedMarkets({ commit }) {
        return commit("CLEAR_SELECTED_MARKETS");
    },
    setSelectedMarket({ commit }, { market, index }) {
        return commit("SET_SELECTED_MARKET", { market, index });
    },
    setAutoDetectedNearestMarket({ commit }, { market, index }) {
        return commit("SET_AUTO_DETECTED_NEAREST_MARKET", { market, index });
    },
    setSelectedGarage({ commit }, { garage, index }) {
        return commit("SET_SELECTED_GARAGE", { garage, index });
    },
    linkQuoteToContract(store, params) {
        return axios.post(`/quotes/${params.quoteId}/linkQuoteToContract/${params.contractId}`);
    },
    setShouldSplitTrip({ commit }, value) {
        return commit("SET_SHOULD_SPLIT_TRIP", value);
    },
    getSplitTrips(_, payload) {
        return axios.post(`/v2/quotes/split`, payload);
    },
    getMarketplaceLink(store, quoteId) {
        return axios.get(`/quotes/marketplaceLink/${quoteId}`);
    },
    updateQuoteMarketplace(store, payload) {
        return axios.post(`/quoteDetail/marketplace`, payload);
    },
    updateQuoteSentBy(store, payload) {
        return axios.post(`/quoteDetail/sentBy`, payload);
    },
    updateQuoteCRMDetails(store, payload) {
        return axios.post(`/quoteDetail/crmDetails`, payload);
    },
    updateConvertedQuote(store, payload) {
        return axios.put(`/quotes/convertedQuote/${payload.quoteId}`, payload);
    },
    addPendingQuoteContact({ commit }, { customerRoles, customer }) {
        commit("ADD_PENDING_QUOTE_CONTACT", { customerRoles, customer });
    },
    editPendingQuoteContact({ commit }, { customer, customerRoles }) {
        commit("EDIT_PENDING_QUOTE_CONTACT", { customer, customerRoles });
    },
    removePendingQuoteContact({ commit }, { customer }) {
        commit("REMOVE_PENDING_QUOTE_CONTACT", { customer });
    },
    clearPendingQuoteContacts({ commit }) {
        commit("CLEAR_PENDING_QUOTE_CONTACTS");
    },
    async applyPendingQuoteContacts({ state, dispatch }, quoteExternalId) {
        if (!quoteExternalId || Object.keys(state.pendingQuoteContacts).length === 0) return;

        const contactPromises = Object.keys(state.pendingQuoteContacts).map((contactKey) => {
            const contact = state.pendingQuoteContacts[contactKey];
            return addQuoteContact(quoteExternalId, contact.customerRoles).catch((error) => {
                showAlert({
                    message: "Error adding Authorized User.",
                    type: "error",
                });
            });
        });

        await Promise.all(contactPromises);
        dispatch("clearPendingQuoteContacts");
    },
    // Line items action for bid/category quotes with new charge model
    async calculateAllTripsLineItems({ commit }, { pricingMethod, trips }) {
        commit("SET_LINE_ITEMS_LOADING", true);
        commit("SET_LINE_ITEMS_ERROR", null);

        try {
            // Build request with all trips
            const request = {
                trips: trips.map((trip, index) => ({
                    tripId: index,
                    tripTotal: 0,
                    charges: trip.charges || [],
                    baseFareForOperator: trip.baseFareForOperator,
                    recurrenceCount: trip.recurrenceCount,
                    showMarkupSeparately: trip.showMarkupSeparately !== undefined ? trip.showMarkupSeparately : true,
                })),
            };

            // Make single API call for all trips
            const response = await lineItemService.calculate(pricingMethod, request);
            const { tripLineItems, quoteTotal } = response.data;

            // Store line items for each trip
            const tripLineItemsMap = {};
            const tripTotalsMap = {};
            tripLineItems.forEach((tripData) => {
                tripLineItemsMap[tripData.tripId] = tripData.lineItems;
                tripTotalsMap[tripData.tripId] = tripData.tripTotal;
            });

            commit("SET_ALL_TRIP_LINE_ITEMS", tripLineItemsMap);
            commit("SET_TRIP_TOTALS", tripTotalsMap);
            commit("SET_LINE_ITEMS_QUOTE_TOTAL", quoteTotal || 0);

            // Return the data for use in components
            return {
                tripLineItems: tripLineItemsMap,
                quoteTotal: quoteTotal || 0,
                tripTotals: tripTotalsMap,
            };
        } catch (error) {
            const errorMessage = error?.response?.data?.message || "Failed to calculate line items";
            commit("SET_LINE_ITEMS_ERROR", errorMessage);
            throw error;
        } finally {
            commit("SET_LINE_ITEMS_LOADING", false);
        }
    },
};

// Private helpers

function getSavedState(key) {
    return JSON.parse(window.localStorage.getItem(key));
}

function saveState(key, state) {
    window.localStorage.setItem(key, JSON.stringify(state));
}

function deleteState(key) {
    window.localStorage.removeItem(key);
}
